import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    KpiBand, KpiSection, StackBar, Buckets, Bucket, Tooltip,
} from '../ui';
import {
    LOT_VALUE_TIPS, buildLotValueView, formatMoneyShort, formatMoneySigned, lotCoverageText,
} from '../../utils/lotValue';
import { computeRiskStats, RISK_TIER_LABELS, RISK_TIER_KEYS } from '../../utils/inventoryView';
import { DEFAULT_AGING } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

// The aging tiles for the dealership's buckets (labels as on the current
// page, "0–30 Days"), with v2 tones; `bucket` is the bucket id the filter holds.
export const agingTiles = (buckets) => buckets.map((b) => ({
    key: b.key, label: b.tileLabel, bucket: b.id, tone: b.tone,
}));
export const AGING_TILES = agingTiles(DEFAULT_AGING.buckets);

export const RISK_TONES = { Red: 'red', Yellow: 'amber', Green: 'sage' };
const RISK_TILES = RISK_TIER_KEYS.map((key) => ({ key, label: RISK_TIER_LABELS[key], tone: RISK_TONES[key] }));

/**
 * The KPI band. Aging, sell-speed risk and lot value read the FILTERED units
 * (today's scope: they ignore the Open Tasks toggle). Aging buckets are a
 * multi-select filter, risk tiers a single-select one. The lot Gap figure
 * sorts the table by AI gap (I7) and back to the default sort.
 */
function InventoryKpis({
    stats, units, activeAgingBuckets, activeRiskTier, onAgingClick, onRiskTierClick, gapSorted, onGapClick,
}) {
    const { aging, gapThresholds } = useDealershipSettings();
    const risk = useMemo(() => computeRiskStats(units, RISK_TILES), [units]);
    const lot = useMemo(() => buildLotValueView(units, gapThresholds), [units, gapThresholds]);

    const tiles = agingTiles(aging.buckets).map((t) => {
        const count = stats[t.key];
        const pct = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : '0.0';
        return { ...t, count, pct };
    });

    // Asking vs AI as one bar: the AI share in sage, the part above (or
    // below) it in amber (teal). Decorative; the figures carry the numbers.
    const lotMax = Math.max(lot.askingTotal, lot.aiTotal) || 1;
    const lotBase = (Math.min(lot.askingTotal, lot.aiTotal) / lotMax) * 100;

    return (
        <KpiBand label="Inventory summary" className="inv2-kpis">
            <KpiSection
                title="Inventory Aging"
                meta={<><b>{stats.total.toLocaleString('en-US')}</b> units</>}
                bar={<StackBar segments={tiles.map((t) => ({ key: t.key, tone: t.tone, value: t.count }))} />}
            >
                <Buckets columns={4}>
                    {tiles.map((t) => (
                        <Bucket
                            key={t.key}
                            value={t.count.toLocaleString('en-US')}
                            label={t.label}
                            sub={`${t.pct}%`}
                            tone={t.tone}
                            pressed={activeAgingBuckets.includes(t.bucket)}
                            onClick={() => onAgingClick(t.bucket)}
                        />
                    ))}
                </Buckets>
            </KpiSection>

            {risk.total > 0 && (
                <KpiSection
                    title="Sell-Speed Risk"
                    meta={(
                        <>
                            <b>{risk.total.toLocaleString('en-US')}</b> scored
                            {risk.highCount > 0 && (
                                <> · <strong className="inv2-high">{risk.highCount.toLocaleString('en-US')} at high risk</strong></>
                            )}
                        </>
                    )}
                    bar={<StackBar segments={risk.tiers.map((t) => ({ key: t.key, tone: t.tone, value: t.count }))} />}
                >
                    <Buckets columns={3}>
                        {risk.tiers.map((t) => {
                            const active = activeRiskTier === t.key;
                            return (
                                <Bucket
                                    key={t.key}
                                    value={t.count.toLocaleString('en-US')}
                                    label={t.label}
                                    sub={`${t.pct}%`}
                                    tone={t.tone}
                                    pressed={active}
                                    onClick={() => onRiskTierClick(active ? 'All' : t.key)}
                                />
                            );
                        })}
                    </Buckets>
                </KpiSection>
            )}

            <KpiSection
                title="Used lot value"
                className="inv2-lotk"
                info={lot.emptyText ? null : <Tooltip label="Coverage" content={lotCoverageText(lot)} />}
                meta={lot.emptyText ? null : `${lot.pricedCount.toLocaleString('en-US')} of ${lot.usedCount.toLocaleString('en-US')} priced`}
                bar={lot.emptyText ? null : (
                    <div className="inv2-lotbar" aria-hidden="true">
                        <span className="inv2-lotbar-ai" style={{ width: `${lotBase}%` }} />
                        <span
                            className={`inv2-lotbar-gap ${lot.askingTotal >= lot.aiTotal ? 'is-over' : 'is-under'}`}
                            style={{ left: `${lotBase}%`, width: `${100 - lotBase}%` }}
                        />
                    </div>
                )}
            >
                {lot.emptyText ? (
                    <p className="inv2-lot-empty">{lot.emptyText}</p>
                ) : (
                    <div className="inv2-lot">
                        <Tooltip content={LOT_VALUE_TIPS.asking}>
                            <div className="inv2-fig" tabIndex={0} data-testid="lot-asking">
                                <span className="inv2-fig-l">Asking</span>
                                <span className="inv2-fig-n">{formatMoneyShort(lot.askingTotal)}</span>
                                <span className="inv2-fig-p">list prices</span>
                            </div>
                        </Tooltip>
                        <Tooltip content={LOT_VALUE_TIPS.ai}>
                            <div className="inv2-fig" tabIndex={0} data-testid="lot-ai">
                                <span className="inv2-fig-l">AI value</span>
                                <span className="inv2-fig-n">{formatMoneyShort(lot.aiTotal)}</span>
                                <span className="inv2-fig-p">model estimate</span>
                            </div>
                        </Tooltip>
                        <Tooltip content={LOT_VALUE_TIPS.gap}>
                            <button
                                type="button"
                                className={`inv2-fig inv2-gap inv2-gap--${lot.severity}`}
                                data-testid="lot-gap"
                                aria-pressed={gapSorted}
                                onClick={onGapClick}
                            >
                                <span className="inv2-fig-l">Gap</span>
                                <span className="inv2-fig-n">{formatMoneySigned(lot.gapTotal)}</span>
                                <span className="inv2-fig-p">
                                    <span className="inv2-gap-pct">{lot.pctText}</span>
                                    {lot.hint && <span data-testid="lot-gap-hint"> {lot.hint}</span>}
                                    <span className="inv2-gap-sort"> · sort by gap</span>
                                </span>
                            </button>
                        </Tooltip>
                    </div>
                )}
            </KpiSection>
        </KpiBand>
    );
}

InventoryKpis.propTypes = {
    stats: PropTypes.shape({
        total: PropTypes.number.isRequired,
        fresh: PropTypes.number.isRequired,
        normal: PropTypes.number.isRequired,
        slow: PropTypes.number.isRequired,
        critical: PropTypes.number.isRequired,
    }).isRequired,
    units: PropTypes.arrayOf(PropTypes.object).isRequired,
    activeAgingBuckets: PropTypes.arrayOf(PropTypes.number).isRequired,
    activeRiskTier: PropTypes.string,
    onAgingClick: PropTypes.func.isRequired,
    onRiskTierClick: PropTypes.func.isRequired,
    gapSorted: PropTypes.bool.isRequired,
    onGapClick: PropTypes.func.isRequired,
};

export default InventoryKpis;
